import {
  AdminImageInput,
  AdminInput,
  AdminPhoneInput,
  AdminSwitch,
  AutoPayoutType,
  Box,
  Button,
  Disclaimer,
  Div,
  ErrorToast,
  ensurePayoutConfigs,
  H3,
  handleChangeNumInputNoDecimalsMinMax,
  lightTheme,
  LocationEntity,
  LocationMode,
  ModalPortal,
  P,
  PayoutConfigEditor,
  PayoutConfigResponse,
  PropsModal,
  Radio,
  Referral,
  reportError,
  Separator,
  SubTitleWithTooltipIcon,
  SuccessToast,
  toPayoutConfigsPayload,
  updateLocationByIdV2,
  updateLocationGoogleBusinessUrl,
  googleBusinessUrlChange,
  GOOGLE_BUSINESS_URL_TOOLTIP,
  UpdateLocationRequestData,
  useAuth,
  UserRole,
  validatePayoutConfigs,
} from '@vgrubs/components';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Collapsible } from '../VDrive/VDriveLocation';
import { ChatTriagePilotToggle } from './ChatTriagePilotToggle';
import { EditReferralModal, ReferralComponent } from './ReferralEdit';
import { ACH_CUSTOMER_ID_TOOLTIP } from '../VDrive/vDriveHelpers';

export const LocationEdit = ({
  modalIsOpen,
  closeModal,
  setLoading,
  data,
  setChangeAddressModal,
  getData,
}: PropsModal & {
  modalIsOpen: boolean;
  data: LocationEntity | null;
  setChangeAddressModal: Dispatch<SetStateAction<boolean>>;
  getData: () => Promise<void>;
}) => {
  const [locationName, setLocationName] = useState('');
  const [payoutConfigs, setPayoutConfigs] = useState<PayoutConfigResponse[]>(
    []
  );
  // What the API has, for the payout editor to restore exceptions from when a
  // model switch lands back on the stored model.
  const savedPayoutConfigs = useMemo(
    () => ensurePayoutConfigs(data?.payoutConfigs),
    [data?.payoutConfigs]
  );
  const [payoutDelayHours, setPayoutDelayHours] = useState<number>(0);
  const [menuManager, setMenuManager] = useState('');
  const [vOrdersOrderingUrl, setvOrdersOrderingUrl] = useState('');
  const [googleBusinessUrl, setGoogleBusinessUrl] = useState('');
  const [menuConsultant, setMenuConsultant] = useState('');
  const [menuAssistant, setMenuAssistant] = useState('');
  const [menuPictureAssistant, setMenuPictureAssistant] = useState('');
  const [image, setImage] = useState<string | File | Blob | null>(null);

  const [referralModal, setReferralModal] = useState(false);

  const [referralUser, setReferralUser] = useState<Referral | null>(null);
  const [achCustomerId, setAchCustomerId] = useState<string>('');
  const [includeServerFeeDeduction, setIncludeServerFeeDeduction] =
    useState(false);
  const [includeDiscountDeduction, setIncludeDiscountDeduction] =
    useState(false);
  const [autoWithdrawalCollapse, setAutoWithdrawalCollapse] = useState(false);
  const [autoWithdrawal, setAutoWithdrawal] = useState<AutoPayoutType>(
    AutoPayoutType.none
  );
  const refAutoWithdrawal = useRef<HTMLDivElement>(null);
  const [elementHeightAutoWithdrawal, setElementHeightAutoWithdrawal] =
    useState(refAutoWithdrawal?.current?.clientHeight);
  const [includeVDrivePayoutDeduction, setIncludeVDrivePayoutDeduction] =
    useState(false);
  const [reportBasedPayout, setReportBasedPayout] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  // POS mode is never picked by hand - the backend sets it when a POS is
  // connected - so the only move on offer here is the way back to tablet.
  const [switchToTabletMode, setSwitchToTabletMode] = useState(false);

  const user = useAuth((state) => state.user);

  useEffect(() => {
    if (data) {
      setLocationName(data.name);
      setPayoutConfigs(ensurePayoutConfigs(data.payoutConfigs));
      setPayoutDelayHours(data.payoutDelayHours);
      setReferralUser(data.referral ?? null);
      setAchCustomerId(data.achCustomerId ?? '');
      setMenuConsultant(data.menuConsultant ?? '');
      setMenuManager(data.menuInfo.manager ?? '');
      setMenuAssistant(data.menuInfo.assistant ?? '');
      setMenuPictureAssistant(data.menuInfo.pictureAssistant ?? '');
      setElementHeightAutoWithdrawal(refAutoWithdrawal?.current?.clientHeight);
      setAutoWithdrawal(data.autoPayoutType ?? AutoPayoutType.none);
      setAutoWithdrawalCollapse(data.autoPayoutType !== AutoPayoutType.none);
      setIncludeServerFeeDeduction(data.includeServerFeeDeduction ?? false);
      setIncludeDiscountDeduction(data.includeDiscountDeduction ?? false);
      setIncludeVDrivePayoutDeduction(
        data.includeVDrivePayoutDeduction ?? false
      );
      setReportBasedPayout(data.reportBasedPayout ?? false);
      setImage(data.imageUrl ?? null);
      setvOrdersOrderingUrl(data.vOrdersOrderingUrl ?? '');
      setGoogleBusinessUrl(data.googleBusinessUrl ?? '');
      setPhoneNumber(data.phone ?? '');
      setSwitchToTabletMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIsOpen]);

  const isAdmin = user?.role === UserRole.admin;

  // Payout-time deductions cannot be edited once earnings come from the
  // provider's payment report. Both switches keep showing the stored value -
  // and still send it on save - they just cannot be toggled while Payout from
  // Report is on.
  const payoutDeductionsLocked = reportBasedPayout;

  async function handleAddEdit() {
    if (!data) return;
    if (autoWithdrawalCollapse && autoWithdrawal === AutoPayoutType.none)
      return ErrorToast(
        `Please select auto withdrawal type in order to enable auto withdrawal.`
      );

    if (isAdmin) {
      const payoutError = validatePayoutConfigs(payoutConfigs);
      if (payoutError) return ErrorToast(payoutError);
    }

    const google = googleBusinessUrlChange(
      data.googleBusinessUrl,
      googleBusinessUrl
    );
    if (google.kind === 'invalid') {
      return ErrorToast('The Google review link must start with https://');
    }

    const loc: UpdateLocationRequestData = {
      name: locationName,
      payoutDelayHours: Number(payoutDelayHours),
      referralId: referralUser?.id || null,
      achCustomerId,
      menuConsultant,
      menuAssistant,
      menuPictureAssistant,
      menuManager,
      autoPayoutType: autoWithdrawal,
      // Payout config is admin only - leaving it out keeps the current config.
      ...(isAdmin
        ? { payoutConfigs: toPayoutConfigsPayload(payoutConfigs) }
        : {}),
      includeServerFeeDeduction,
      includeDiscountDeduction,
      includeVDrivePayoutDeduction,
      reportBasedPayout,
      vOrdersOrderingUrl,
      phoneNumber: phoneNumber ? phoneNumber : null,
      // Left out unless asked for: sending the current mode back is a 400.
      ...(switchToTabletMode ? { mode: LocationMode.tablet } : {}),
    };

    const formData = new FormData();

    formData.append('location', JSON.stringify(loc));
    if (image && typeof image !== 'string') {
      formData.append('image', image as File);
    }

    try {
      setLoading?.(true);
      const res = await updateLocationByIdV2(data.id, formData);
      // Its own route: the location form does not carry it.
      if (google.kind === 'set') {
        await updateLocationGoogleBusinessUrl(data.id, google.value);
      }
      await getData();
      SuccessToast(res.data.message);
      closeModal?.();

      setLoading?.(false);
    } catch (error) {
      reportError(error);
      setLoading?.(false);
    }
  }

  const handleChangeAutoWithdrawalCollapse = (v: boolean) => {
    setAutoWithdrawalCollapse(v);
    if (!v) {
      if (
        autoWithdrawal === AutoPayoutType.weekly ||
        autoWithdrawal === AutoPayoutType.monthly
      ) {
        // handleAutoWithdrawal(AutoPayoutType.none);
        setAutoWithdrawal(AutoPayoutType.none);
      }
    }
  };

  const handleChangeAutoWithdrawal = (v: AutoPayoutType) => {
    setAutoWithdrawal(v);
    // handleAutoWithdrawal(v);
  };

  return (
    <>
      <ModalPortal
        isOpen={modalIsOpen}
        closeModal={closeModal}
        logo={false}
        header="Edit Location"
        modalOverflow="visible"
        width="600px"
      >
        <Div $flex $column $gap="24px">
          <AdminImageInput
            image={image ?? (data?.imageUrl as string | null)}
            onChange={(img) => setImage(img)}
            label="Location Image"
            textCenter
            shape="circle"
            width="160px"
          />
          <AdminInput
            label="Location Name"
            placeholder="Enter here"
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
            $textCenter
          />

          <AdminPhoneInput
            label="Phone Number"
            value={phoneNumber}
            onChange={setPhoneNumber}
            $textCenter
          />

          {data?.mode === LocationMode.pos && (
            <Box $flex $column $justifyCenter $width="100%">
              <Div $flex $alignCenter $between $gap="16px">
                <SubTitleWithTooltipIcon
                  mb="0"
                  text={'Switch to Tablet Mode'}
                  tooltip={`
                    This location runs in POS mode. POS mode is set automatically when a POS is connected, so it cannot be picked here. Turn this on and save to move the location back to tablet mode - it only goes through once the POS is disconnected.
                `}
                />

                <AdminSwitch
                  checked={switchToTabletMode}
                  onChange={() => setSwitchToTabletMode((prev) => !prev)}
                />
              </Div>
              {switchToTabletMode && (
                <Disclaimer $m="0" $mt="8" $left $color={lightTheme.lightGray}>
                  The location moves to tablet mode on save. Disconnect the POS
                  first, otherwise the save is rejected.
                </Disclaimer>
              )}
            </Box>
          )}

          {isAdmin && (
            <Div>
              <SubTitleWithTooltipIcon
                mb="8"
                text={'Payout Configuration'}
                tooltip={`
                    Sets how each service pays out this location. The payout is calculated on the reported amount - the payout reported by the marketplace when available (currently Uber Eats and Grubhub), otherwise the order subtotal. <b>Bracket</b> pays the restaurant owner a percentage of that amount (enter the percentage the owner should receive). <b>Flat Fee</b> is a fixed amount per order. <b>Hybrid</b> combines the two - a fixed amount and/or a bracket percentage on top - and can optionally change depending on the order's reported amount (for example, a flat fee on small orders and a bracket on larger ones).
                `}
              />
              <PayoutConfigEditor
                configs={payoutConfigs}
                savedConfigs={savedPayoutConfigs}
                onChange={setPayoutConfigs}
              />
            </Div>
          )}

          <AdminInput
            tooltip={ACH_CUSTOMER_ID_TOOLTIP}
            $textCenter
            label="ACH Customer ID"
            placeholder="Enter here"
            value={achCustomerId}
            onChange={({ target }) => setAchCustomerId(target.value)}
          />

          <AdminInput
            label="vOrders Direct Ordering URL"
            placeholder="Enter here"
            value={vOrdersOrderingUrl}
            onChange={(e) => setvOrdersOrderingUrl(e.target.value)}
            $textCenter
            tooltip="This link is used for vOrders. When customers opt in on the vDrive tracking page, they will receive this direct ordering link via SMS."
          />

          <AdminInput
            label="Google Review Link"
            placeholder="https://g.page/r/..."
            value={googleBusinessUrl}
            onChange={(e) => setGoogleBusinessUrl(e.target.value)}
            $textCenter
            tooltip={GOOGLE_BUSINESS_URL_TOOLTIP}
          />

          <AdminInput
            label="Menu Manager"
            placeholder="Enter here"
            value={menuManager}
            onChange={(e) => setMenuManager(e.target.value)}
            $textCenter
          />

          <AdminInput
            label="Menu Consultant"
            placeholder="Enter here"
            value={menuConsultant}
            onChange={(e) => setMenuConsultant(e.target.value)}
            $textCenter
          />

          <AdminInput
            label="Menu Assistant"
            placeholder="Enter here"
            value={menuAssistant}
            onChange={(e) => setMenuAssistant(e.target.value)}
            $textCenter
          />

          <AdminInput
            label="Menu Picture Assistant"
            placeholder="Enter here"
            value={menuPictureAssistant}
            onChange={(e) => setMenuPictureAssistant(e.target.value)}
            $textCenter
          />
        </Div>
        {isAdmin && (
          <Box
            $flex
            $column
            $p="24px"
            $pSM="20px"
            $relative
            $width="100%"
            $mb="24"
            $mt="24"
          >
            <H3 $left $m="0" $mb="16">
              vPay Wallet
            </H3>

            <AdminInput
              label="Payout Delay (hours)"
              placeholder="Enter here"
              value={payoutDelayHours}
              onChange={(e) =>
                handleChangeNumInputNoDecimalsMinMax(
                  e,
                  setPayoutDelayHours,
                  0,
                  200
                )
              }
              $textCenter
              tooltip="Time delay between order completion and payout to the restaurant owner."
            />

            <Div $flex $alignCenter $between $gap="16px" $mt="24">
              <SubTitleWithTooltipIcon
                mb="0"
                text={'Auto Withdrawal'}
                tooltip={`
                     Enable this option to automate payouts to the client’s linked bank account. Funds will be transferred automatically on the selected schedule (weekly or monthly), so the client doesn’t need to manually initiate a withdrawal from the app.
                `}
              />

              <AdminSwitch
                checked={autoWithdrawalCollapse}
                onChange={() =>
                  handleChangeAutoWithdrawalCollapse(!autoWithdrawalCollapse)
                }
              />
            </Div>
            <Collapsible
              expanded={autoWithdrawalCollapse}
              elementHeight={elementHeightAutoWithdrawal ?? 0}
            >
              <Div ref={refAutoWithdrawal} $flex $column $gap="16px">
                <Div
                  $pointer
                  $flex
                  $alignCenter
                  $between
                  onClick={() =>
                    handleChangeAutoWithdrawal(AutoPayoutType.weekly)
                  }
                >
                  <P $m="0">Every Week (Monday)</P>
                  <Radio checked={autoWithdrawal === AutoPayoutType.weekly} />
                </Div>
                <Div
                  $pointer
                  $flex
                  $alignCenter
                  $between
                  onClick={() =>
                    handleChangeAutoWithdrawal(AutoPayoutType.monthly)
                  }
                >
                  <P $m="0">Every Month (1st of the month)</P>
                  <Radio checked={autoWithdrawal === AutoPayoutType.monthly} />
                </Div>
                <Separator />
              </Div>
            </Collapsible>

            <Div $flex $alignCenter $between $gap="16px" $mt="24">
              <SubTitleWithTooltipIcon
                mb="0"
                text={'Payout from Report'}
                tooltip={`
                    When enabled, earnings are calculated from the provider’s payment report instead of order completion time. The report lands 3 days after the order is completed - the same for both providers. The exact date is shown on each pending earning. Payout Delay is applied on top of the report date. Available for Uber Eats and Grubhub.
                `}
              />

              <AdminSwitch
                checked={reportBasedPayout}
                onChange={() => setReportBasedPayout((prev) => !prev)}
              />
            </Div>

            {/* <Div $flex $alignCenter $between $gap="16px" $mt="24">
              <SubTitleWithTooltipIcon
                mb="0"
                text={'Include Server Fee Deduction'}
                tooltip={`
                    This will deduct the 3,26% of total amount from the payout amount.
                `}
              />

              <AdminSwitch
                checked={includeServerFeeDeduction}
                onChange={() =>
                  setIncludeServerFeeDeduction(!includeServerFeeDeduction)
                }
              />
            </Div> */}
            <Div $flex $alignCenter $between $gap="16px" $mt="24">
              <SubTitleWithTooltipIcon
                mb="0"
                text={'Include Discount Fee Deduction'}
                textColor={
                  payoutDeductionsLocked ? lightTheme.lightGray : undefined
                }
                color={
                  payoutDeductionsLocked ? lightTheme.lightGray : undefined
                }
                tooltip={`
                   This will deduct the order discount from the payout amount. 
                `}
              />

              <AdminSwitch
                checked={includeDiscountDeduction}
                disabled={payoutDeductionsLocked}
                onChange={() =>
                  setIncludeDiscountDeduction(!includeDiscountDeduction)
                }
              />
            </Div>

            {/* <Div $flex $alignCenter $between $gap="16px" $mt="24">
              <SubTitleWithTooltipIcon
                mb="0"
                text={'vDrive Delivery Cost Sharing (Bracket Deduction)'}
                textColor={
                  payoutDeductionsLocked ? lightTheme.lightGray : undefined
                }
                color={
                  payoutDeductionsLocked ? lightTheme.lightGray : undefined
                }
                tooltip={`
                 When a delivery costs more than the diner’s delivery fee, the remaining cost is split between vGrubs and the store owner using the same bracket percentage as order payouts.
                  <br/><br/>
                  <b>Example:</b>
                  If delivery costs $8 and the diner pays $3, the remaining $5 is split.
                  With a 50% bracket, $2.50 is deducted from the owner’s payout and $2.50 is covered by vGrubs.
                  <br/><br/>
                  <b>This works the same way as order revenue splitting (e.g. a $50 order is split $25 / $25).</b>
                `}
              />

              <AdminSwitch
                checked={includeVDrivePayoutDeduction}
                disabled={payoutDeductionsLocked}
                onChange={() =>
                  setIncludeVDrivePayoutDeduction(!includeVDrivePayoutDeduction)
                }
              />
            </Div> */}
            {payoutDeductionsLocked && (
              <Disclaimer $m="0" $mt="8" $left $color={lightTheme.lightGray}>
                Both deductions are locked while Payout from Report is on - the
                payout then follows the provider&apos;s payment report.
              </Disclaimer>
            )}
          </Box>
        )}

        {/* Admin only: turning the assistant loose on a merchant's messages
            is a rollout decision, not a support one. */}
        {isAdmin && data && <ChatTriagePilotToggle locationId={data.id} />}

        <ReferralComponent
          referral={referralUser}
          setModalIsOpen={setReferralModal}
        />

        <Button
          $gray
          $w="100%"
          $mt="50"
          $mtSM="30"
          onClick={() => {
            setChangeAddressModal(true);
            closeModal?.();
          }}
        >
          Change Location Address
        </Button>

        <Button
          $selected
          $w="100%"
          $mt="16"
          // $mtSM="30"
          onClick={() => handleAddEdit()}
        >
          Submit
        </Button>
      </ModalPortal>

      <EditReferralModal
        modalIsOpen={referralModal}
        closeModal={() => {
          setReferralModal(false);
        }}
        setReferralUser={setReferralUser}
        referralUser={referralUser}
      />
    </>
  );
};
